import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useSortBy } from "web/react/hooks/filters/";
import { setFeedOrder } from "web/redux/ducks/feed-filters";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import styles from "./feed-sort.module.css";

const AnalyticsActions = {
    newest: "SORT_BY_NEWEST",
    price_desc: "SORT_BY_HIGH_LOW",
    price_asc: "SORT_BY_LOW_HIGH",
    recommended: "SORT_BY_RECOMMENDED",
    discount_desc: "SORT_BY_DISCOUNT_HIGH_LOW",
};

interface SortOption {
    label: string;
    value: string;
    selected: boolean;
    url: string | null;
}

export interface FeedSortProps {
    sortOptions: SortOption[];
}

export function FeedSort({ sortOptions }: FeedSortProps): JSX.Element {
    const sort = useSelector((state: ReduxStoreState) => state.feedFiltersReducer.view);
    const feedPreFilters = useSelector((state: ReduxStoreState) => state.feed.data.pre_filters);
    const dispatch = useDispatch();
    const isMobileFilterRedesignEnabled = environment.getFeature(
        "uf_enable_redesign_mobile_filter"
    );
    const { onChange: onSortByChange } = useSortBy();

    const selected = useMemo(() => {
        for (let option of sortOptions) {
            if (typeof sort === "undefined" && option.selected) {
                return option;
            } else if (option.value === sort) {
                return option;
            }
        }
        // Fallback for when bad things happen
        return sortOptions[0];
    }, [sort, sortOptions]);

    function onChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        const sort = event.target.value;
        analytics.event("sort_by", AnalyticsActions[sort]);
        const sortOption = sortOptions?.find((sortOption) => sortOption.value === sort);
        if (
            sortOption &&
            sortOption.value === "newest" &&
            sortOption.url &&
            feedPreFilters &&
            !feedPreFilters.subcategory
        )
            window.location = sortOption?.url as any;
        else {
            // Support parity with the redesigned mobile filters
            if (isMobileFilterRedesignEnabled) {
                onSortByChange(sort);
            }

            dispatch(setFeedOrder(sort));
        }
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.select}>
                <select
                    aria-label={gettext("feed.sort.mobile_btn_text")}
                    value={selected.value}
                    onChange={onChange}
                >
                    {sortOptions.map(({ value, label }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>
            <button
                type="button"
                className={styles.mobile}
                aria-label={gettext("feed.sort.mobile_btn_text")}
            >
                <SVGIcon name="sort" />
            </button>
        </div>
    );
}
